import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Bell, X, Check, CheckCheck, FileText, Scale, AlertTriangle, Paperclip, MessageCircle, Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = process.env.REACT_APP_BACKEND_URL

const TYPE_ICONS = {
  nouveau_sinistre: FileText,
  recommandation_doctrine: Scale,
  sla_depasse: AlertTriangle,
  document_ajoute: Paperclip,
  commentaire_ajoute: MessageCircle,
  inactivite_dossier: Clock,
}

const TYPE_COLORS = {
  nouveau_sinistre: 'text-blue-500 bg-blue-50',
  recommandation_doctrine: 'text-purple-500 bg-purple-50',
  sla_depasse: 'text-red-500 bg-red-50',
  document_ajoute: 'text-emerald-500 bg-emerald-50',
  commentaire_ajoute: 'text-amber-500 bg-amber-50',
  inactivite_dossier: 'text-orange-500 bg-orange-50',
}

function timeAgo(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60) return "À l'instant"
  if (diff < 3600) return `${Math.floor(diff / 60)}min`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}j`
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

export default function NotificationBell() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [count, setCount] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const panelRef = useRef(null)

  const userId = profile?.id

  const fetchCount = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch(`${API_URL}/api/notifications/count?user_id=${userId}`)
      if (res.ok) {
        const data = await res.json()
        setCount(data.count || 0)
      }
    } catch {}
  }, [userId])

  const fetchNotifications = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/notifications?user_id=${userId}&limit=20`)
      if (res.ok) {
        const data = await res.json()
        setNotifications(data.notifications || [])
      }
    } catch {} finally {
      setLoading(false)
    }
  }, [userId])

  // Poll count every 30 seconds
  useEffect(() => {
    fetchCount()
    const interval = setInterval(fetchCount, 30000)
    return () => clearInterval(interval)
  }, [fetchCount])

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) fetchNotifications()
  }, [open, fetchNotifications])

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  async function markRead(notifId) {
    await fetch(`${API_URL}/api/notifications/${notifId}/read?user_id=${userId}`, { method: 'POST' })
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, lu: true } : n))
    setCount(prev => Math.max(0, prev - 1))
  }

  async function markAllRead() {
    await fetch(`${API_URL}/api/notifications/read-all?user_id=${userId}`, { method: 'POST' })
    setNotifications(prev => prev.map(n => ({ ...n, lu: true })))
    setCount(0)
  }

  function handleClick(notif) {
    if (!notif.lu) markRead(notif.id)
    if (notif.lien) {
      navigate(notif.lien)
      setOpen(false)
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={18} className="text-slate-300" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
            data-testid="notification-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed left-[240px] top-2 w-[380px] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors"
                  data-testid="mark-all-read"
                >
                  <CheckCheck size={13} />
                  Tout lire
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-slate-200 transition-colors"
              >
                <X size={14} className="text-slate-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">Chargement...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Aucune notification</p>
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = TYPE_ICONS[notif.type] || Bell
                const colorClass = TYPE_COLORS[notif.type] || 'text-slate-500 bg-slate-50'

                return (
                  <div
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer transition-colors ${
                      notif.lu ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/40 hover:bg-blue-50/60'
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs leading-snug ${notif.lu ? 'text-slate-600' : 'text-slate-800 font-semibold'}`}>
                          {notif.titre}
                        </p>
                        {!notif.lu && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-slate-300 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
